import { useCallback, useEffect, useMemo, useState } from "react";

import {
  payoutsApi,
  api as accountsApi,
  type Payout,
  type PayoutInput,
  type Account,
  ApiError,
} from "../lib/api";
import PayoutForm from "../components/PayoutForm";
import Modal from "../components/Modal";

type Mode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; payout: Payout };

export default function Payouts() {
  const [payouts, setPayouts] = useState<Payout[] | null>(null);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "closed" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filterAccount, setFilterAccount] = useState<string>("");

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [p, a] = await Promise.all([payoutsApi.list(), accountsApi.list()]);
      setPayouts(p);
      setAccounts(a);
    } catch (e) {
      setError(messageFrom(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const accountLookup = useMemo(() => {
    const map = new Map<string, Account>();
    (accounts ?? []).forEach((a) => map.set(a.account_id, a));
    return map;
  }, [accounts]);

  const visible = useMemo(() => {
    if (!payouts) return null;
    const filtered = filterAccount ? payouts.filter((p) => p.account_id === filterAccount) : payouts;
    return [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [payouts, filterAccount]);

  const total = useMemo(() => {
    if (!visible) return 0;
    return visible.reduce((sum, p) => sum + p.amount, 0);
  }, [visible]);

  async function handleCreate(input: PayoutInput) {
    await payoutsApi.create(input);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleUpdate(id: string, input: PayoutInput) {
    await payoutsApi.update(id, input);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this payout entry? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await payoutsApi.remove(id);
      await refresh();
    } catch (e) {
      setError(messageFrom(e));
    } finally {
      setBusyId(null);
    }
  }

  const sortedAccounts = useMemo(() => {
    return [...(accounts ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  return (
    <section className="accounts">
      <header className="accounts-header">
        <h1>Payouts</h1>
        <div className="header-actions">
          <label className="inline-checkbox">
            <span>Account:</span>
            <select
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
            >
              <option value="">All accounts</option>
              {sortedAccounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.name} ({a.account_number})
                </option>
              ))}
            </select>
          </label>
          <button className="primary" onClick={() => setMode({ kind: "create" })}>
            + New payout
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {visible === null ? (
        <p>Loading&hellip;</p>
      ) : visible.length === 0 ? (
        <p className="empty">
          No payouts{filterAccount ? " for this account" : ""} yet. Click{" "}
          <strong>+ New payout</strong> to add one.
        </p>
      ) : (
        <>
          <p className="muted">
            Showing {visible.length} payout{visible.length === 1 ? "" : "s"}
            {filterAccount ? " for this account" : " across all accounts"} totaling{" "}
            <strong>{formatUsd(total)}</strong>.
          </p>
          <table className="accounts-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Prop firm</th>
                <th>Account</th>
                <th className="num">Amount</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const account = accountLookup.get(p.account_id);
                return (
                  <tr key={p.payout_id}>
                    <td>{formatDate(p.date)}</td>
                    <td>{p.prop_firm}</td>
                    <td>
                      {account ? (
                        <>
                          {account.name}{" "}
                          <span className="mono muted">({account.account_number})</span>
                        </>
                      ) : (
                        <span className="muted">(deleted)</span>
                      )}
                    </td>
                    <td className={`num ${p.amount > 0 ? "pnl-pos" : p.amount < 0 ? "pnl-neg" : ""}`}>
                      {formatUsd(p.amount)}
                    </td>
                    <td className="notes-cell">
                      {p.notes ? p.notes : <span className="muted">—</span>}
                    </td>
                    <td className="row-actions">
                      <button
                        className="link-button"
                        onClick={() => setMode({ kind: "edit", payout: p })}
                      >
                        Edit
                      </button>
                      <button
                        className="link-button danger"
                        disabled={busyId === p.payout_id}
                        onClick={() => handleDelete(p.payout_id)}
                      >
                        {busyId === p.payout_id ? "…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      <Modal open={mode.kind !== "closed"} onClose={() => setMode({ kind: "closed" })}>
        {mode.kind === "create" && (
          <PayoutForm
            title="New payout"
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={handleCreate}
          />
        )}
        {mode.kind === "edit" && (
          <PayoutForm
            title="Edit payout"
            initial={mode.payout}
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={(input) => handleUpdate(mode.payout.payout_id, input)}
          />
        )}
      </Modal>
    </section>
  );
}

function formatDate(value: string): string {
  if (!value) return "—";
  // YYYY-MM-DD comes through verbatim — locale-format it as a date only.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString();
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function formatUsd(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
